/**
 * Authorization conformance suite.
 *
 * These are not illustrations — they execute the real engine against the real
 * store and report what it actually decided. Most of them are deliberate
 * bypass ATTEMPTS: forged roles, borrowed organization ids, revoked
 * memberships, another tenant's campus. Each states the expectation, and a
 * case only passes when the engine refuses for the RIGHT reason, so a change
 * that starts denying everything cannot quietly turn the suite green.
 */
import {
  authorize,
  canEnterWorkspace,
  canGrantRole,
  effectivePublicPermissions,
  internalStaffFor,
  requireInternal,
  resolveWorkspaces,
  type AuthorizationStore,
} from './authorization'
import type { AuthorizationDenial } from '../types/workspace'
import type { Identity } from '../types/auth'

export interface TestCase {
  id: string
  group: string
  name: string
  /** What the case is defending against, in plain language. */
  intent: string
  passed: boolean
  expected: string
  actual: string
}

export interface TestGroupResult {
  group: string
  cases: TestCase[]
  passed: number
  total: number
}

interface Ctx {
  store: AuthorizationStore
  amarachi: Identity
  tunde: Identity
  kelechi: Identity
}

let seq = 0
function make(
  group: string,
  name: string,
  intent: string,
  expected: string,
  actual: string,
): TestCase {
  seq += 1
  return { id: `t-${seq}`, group, name, intent, expected, actual, passed: expected === actual }
}

/** Renders a decision as a short comparable string. */
function outcome(decision: { allowed: boolean; reason: AuthorizationDenial | null }): string {
  return decision.allowed ? 'allowed' : `denied:${decision.reason}`
}

export function runAuthorizationSuite(ctx: Ctx): TestGroupResult[] {
  seq = 0
  const { store, amarachi, tunde, kelechi } = ctx
  const cases: TestCase[] = []

  /* ------------------------------------------------------------- identity */

  cases.push(
    make(
      'Identity',
      'An unauthenticated caller is refused',
      'Authentication is the first layer; no permission survives its absence.',
      'denied:not_authenticated',
      outcome(authorize(store, null, 'self.profile.manage')),
    ),
    make(
      'Identity',
      'A suspended account grants nothing',
      'A valid session on a suspended account must not carry its old access.',
      'denied:account_not_active',
      outcome(authorize(store, kelechi, 'self.profile.manage')),
    ),
    make(
      'Identity',
      'An active identity reaches its own record',
      'The owner of a personal record can act on it.',
      'allowed',
      outcome(authorize(store, amarachi, 'self.profile.manage', { identityId: amarachi.id })),
    ),
    make(
      'Identity',
      'One identity cannot reach another’s personal record',
      'No role, at any level, turns into access to another person’s own data.',
      'denied:not_resource_owner',
      outcome(authorize(store, amarachi, 'self.profile.manage', { identityId: tunde.id })),
    ),
  )

  /* ----------------------------------------------------------- membership */

  const amarachiMemberships = store.memberships.filter((m) => m.identityId === amarachi.id)
  cases.push(
    make(
      'Membership',
      'One identity holds several memberships',
      'Multiple participations must not require multiple accounts.',
      'three or more',
      amarachiMemberships.length >= 3 ? 'three or more' : `${amarachiMemberships.length}`,
    ),
    make(
      'Membership',
      'A suspended membership opens nothing',
      'Suspension must take effect immediately, not at next sign-in.',
      'denied:no_permission',
      outcome(
        authorize(store, tunde, 'provider.orders.view', { organizationId: 'org-brandreach' }),
      ),
    ),
    make(
      'Membership',
      'A revoked membership is kept but grants nothing',
      'History must survive revocation; access must not.',
      'kept and refused',
      store.memberships.some((m) => m.id === 'mem-kelechi-cedarwood') &&
        !canEnterWorkspace(store, kelechi, 'housing').allowed
        ? 'kept and refused'
        : 'record lost or access retained',
    ),
  )

  /* ---------------------------------------------------------- workspaces */

  const spaces = resolveWorkspaces(store, amarachi)
  cases.push(
    make(
      'Workspaces',
      'Authorised workspaces resolve from memberships',
      'The list is derived, never stored, so it cannot drift from membership.',
      'community, provider, student',
      [...new Set(spaces.map((s) => s.kind))].sort().join(', ') || 'none',
    ),
    make(
      'Workspaces',
      'An unauthorised workspace is refused',
      'Holding one workspace never implies another.',
      'denied:no_membership',
      outcome(canEnterWorkspace(store, amarachi, 'campus')),
    ),
    make(
      'Workspaces',
      'Switching workspace does not add permissions',
      'Selecting a workspace is not a grant.',
      'unchanged',
      (() => {
        const before = effectivePublicPermissions(store, amarachi).join('|')
        resolveWorkspaces(store, amarachi).forEach(() => undefined)
        const after = effectivePublicPermissions(store, amarachi).join('|')
        return before === after ? 'unchanged' : 'changed'
      })(),
    ),
    make(
      'Workspaces',
      'A suspended account resolves no workspaces at all',
      'Account standing overrides every membership beneath it.',
      '0',
      `${resolveWorkspaces(store, kelechi).length}`,
    ),
  )

  /* --------------------------------------------------------- permissions */

  cases.push(
    make(
      'Permissions',
      'A held permission succeeds within its organization',
      'The ordinary case must actually work.',
      'allowed',
      outcome(
        authorize(store, amarachi, 'provider.orders.view', {
          organizationId: 'org-amarachi-foods',
        }),
      ),
    ),
    make(
      'Permissions',
      'A permission the role lacks is refused',
      'Roles are bundles, not gateways to a whole domain.',
      'denied:no_permission',
      outcome(
        authorize(store, amarachi, 'housing.properties.manage', {
          organizationId: 'org-amarachi-foods',
        }),
      ),
    ),
    make(
      'Permissions',
      'A campus permission is refused to a provider owner',
      'A commercial role never acquires institutional reach.',
      'denied:no_permission',
      outcome(authorize(store, amarachi, 'campus.students.view')),
    ),
  )

  /* ------------------------------------------------------- multi-tenancy */

  cases.push(
    make(
      'Multi-tenancy',
      'Provider A cannot read Provider B’s orders',
      'Naming another organization’s id is a claim, not access.',
      'denied:not_resource_owner',
      outcome(
        authorize(store, amarachi, 'provider.orders.view', { organizationId: 'org-brandreach' }),
      ),
    ),
    make(
      'Multi-tenancy',
      'A community manager cannot reach another community',
      'Community moderation is scoped to the community, not campus-wide.',
      'denied:not_resource_owner',
      outcome(
        authorize(store, amarachi, 'community.posts.moderate', { organizationId: 'org-unilag' }),
      ),
    ),
    make(
      'Multi-tenancy',
      'Campus scope does not reach another institution',
      'A UNILAG-scoped membership must not resolve against FUTA.',
      'denied:no_permission',
      outcome(
        authorize(store, amarachi, 'campus.verification.decide', {
          institutionId: 'inst-futa',
          campusId: 'cmp-futa-main',
        }),
      ),
    ),
  )

  /* --------------------------------------------------------- forged input */

  cases.push(
    make(
      'Forged input',
      'A forged role on the identity object is ignored',
      'Authorization reads memberships, never a role field a client could set.',
      'denied:no_permission',
      outcome(
        authorize(
          store,
          {
            ...amarachi,
            roles: [...amarachi.roles, 'university_admin', 'campuzon_admin'],
            activeRole: 'campuzon_admin',
          } as Identity,
          'campus.academics.manage',
        ),
      ),
    ),
    make(
      'Forged input',
      'A forged organization id grants nothing',
      'The resource reference is validated, not believed.',
      'denied:not_resource_owner',
      outcome(
        authorize(store, amarachi, 'provider.earnings.view', {
          organizationId: 'org-cedarwood',
        }),
      ),
    ),
    make(
      'Forged input',
      'A forged workspace id selects nothing',
      'Workspace ids are matched against freshly resolved access.',
      'denied:no_membership',
      outcome(canEnterWorkspace(store, amarachi, 'opportunity')),
    ),
    make(
      'Forged input',
      'A member cannot grant a role above their own',
      'Member management must not become privilege escalation.',
      'denied:no_permission',
      outcome(
        canGrantRole(store, amarachi, 'org-amarachi-foods', 'orole-housing-manager'),
      ),
    ),
  )

  /* --------------------------------------------------- internal isolation */

  const internalRoleIsPublic = store.roles.some((role) =>
    role.permissions.some((key) => key.startsWith('finance.') || key.startsWith('staff.')),
  )
  cases.push(
    make(
      'Internal',
      'No public role carries an internal permission',
      'Internal administration must not be expressible in the public register.',
      'none',
      internalRoleIsPublic ? 'some' : 'none',
    ),
    make(
      'Internal',
      'A public account cannot reach internal administration',
      'Internal access comes only from a provisioned staff link.',
      'denied:internal_only',
      outcome(requireInternal(store, tunde)),
    ),
    make(
      'Internal',
      'A forged admin role does not produce internal access',
      'Editing the identity cannot promote it.',
      'denied:internal_only',
      outcome(
        requireInternal(store, {
          ...tunde,
          status: 'active',
          roles: ['student', 'campuzon_admin'],
          activeRole: 'campuzon_admin',
        } as Identity),
      ),
    ),
    make(
      'Internal',
      'A provisioned staff link does produce internal access',
      'The legitimate path must still work.',
      'allowed',
      outcome(requireInternal(store, amarachi)),
    ),
    make(
      'Internal',
      'A suspended account with a staff link is refused',
      'Account standing outranks the staff link.',
      'no staff',
      internalStaffFor(store, { ...amarachi, status: 'suspended' } as Identity) === null
        ? 'no staff'
        : 'staff resolved',
    ),
  )

  /* ------------------------------------------------------------ grouping */

  const order = [
    'Identity',
    'Membership',
    'Workspaces',
    'Permissions',
    'Multi-tenancy',
    'Forged input',
    'Internal',
  ]
  return order.map((group) => {
    const inGroup = cases.filter((item) => item.group === group)
    return {
      group,
      cases: inGroup,
      passed: inGroup.filter((item) => item.passed).length,
      total: inGroup.length,
    }
  })
}
